import { notifications } from '@mantine/notifications';
import { useContext, useState } from 'react';
import { useDeleteFloraPage, useUpdateFloraPage } from '../../../api/flora-pages-client/flora-pages-client.hooks';
import type { FloraPage } from '../../../api/flora-pages-client/flora-pages-client.type';
import { FloraPageVisibility } from '../../../api/flora-pages-client/flora-pages-client.type';
import { UserContext } from '../../../contexts';

/**
 * Ownership check, visibility toggle, and delete-confirmation flow for a
 * single Flora page, shared by both the grid and table views so neither
 * duplicates the mutation wiring or "who can act on this row" logic.
 *
 * @returns {{
 *   isOwner: (page: FloraPage) => boolean,
 *   toggleVisibility: (page: FloraPage) => void,
 *   pageToDelete: FloraPage | null,
 *   requestDelete: (page: FloraPage) => void,
 *   cancelDelete: () => void,
 *   confirmDelete: () => Promise<void> | undefined,
 *   isDeleting: boolean,
 *   deleteError: Error | null,
 * }} Ownership check plus visibility/delete mutation handlers.
 */
export function useFloraPageActions() {
  const { user } = useContext(UserContext);
  const updatePageMutation = useUpdateFloraPage();
  const deletePageMutation = useDeleteFloraPage();
  const [pageToDelete, setPageToDelete] = useState<FloraPage | null>(null);

  const isOwner = (page: FloraPage) => !!user?.id && page.user_id === user.id;

  const toggleVisibility = (page: FloraPage) => {
    const visibility =
      page.visibility === FloraPageVisibility.Organization
        ? FloraPageVisibility.Private
        : FloraPageVisibility.Organization;
    updatePageMutation.mutate(
      { id: page.id, payload: { visibility } },
      {
        onError: () => {
          notifications.show({ message: `Couldn't update "${page.title}". Please try again.`, color: 'red' });
        },
      },
    );
  };

  // `mutateAsync` (not `mutate`) so DeleteConfirmationModal awaits the result
  // before closing — otherwise a failed delete looks identical to a success.
  const confirmDelete = () => (pageToDelete ? deletePageMutation.mutateAsync(pageToDelete.id) : undefined);

  const cancelDelete = () => {
    deletePageMutation.reset();
    setPageToDelete(null);
  };

  return {
    isOwner,
    toggleVisibility,
    pageToDelete,
    requestDelete: (page: FloraPage) => setPageToDelete(page),
    cancelDelete,
    confirmDelete,
    isDeleting: deletePageMutation.isPending,
    deleteError: deletePageMutation.error,
  };
}
